import { statusFetching } from "constants/types";
import { IUserInfo, USER_SCOPE_ENUM, USER_STATUS_ENUM, UserScope } from "interfaces";
import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { HttpResponseError } from "utils";
import { findCertificate } from "utils/processCert";
import { IStores } from "stores";
import { StoreConstructor } from "./StoreConstructor";

export type LoginData = {
  user: string;
  smsCode: string;
  companyId: string;
};

export interface IUserService<T = any> {
  login: (data: LoginData) => Promise<T | null>;
  refreshToken: () => Promise<T>;
  dropToken: () => void;
  logout: () => Promise<void>;
  error: string | null;
}

export class UserStore extends StoreConstructor {
  public userInfo?: IUserInfo;
  public status: statusFetching = "init";
  public isAuthorized: boolean = false;
  public redirectPathname: string = "";
  public isAfterLogout: boolean = false;
  public error: string | null = null;

  userService: IUserService<IUserInfo>;

  constructor(params: { userService: IUserService<IUserInfo> }, stores: IStores) {
    super(stores);
    this.userService = params.userService;

    // Explicitly configure observables and actions
    makeObservable(this, {
      userInfo: observable,
      status: observable,
      isAuthorized: observable,
      redirectPathname: observable,
      error: observable,
      init: action.bound,
      login: action.bound,
      logout: action.bound,
      showError: action.bound,
      initSuccess: action.bound,
      logoutSuccess: action.bound,
      isPending: computed,
      isError: computed,
      scope: computed,
      roles: computed,
    });
  }

  public init() {
    this.status = "fetching";
    this.error = null;

    return this.userService
      .refreshToken()
      .then(this.initSuccess)
      .catch(() => {
        runInAction(() => {
          this.status = "error";
        });
      });
  }

  public login(data: LoginData) {
    return this.userService
      .login(data)
      .then(async (user) => {
        const isCreateCert =
          user &&
          [
            USER_SCOPE_ENUM.JUNIOR_LP,
            USER_SCOPE_ENUM.SENIOR_LP,
            USER_SCOPE_ENUM.JUNIOR_CUSTOMER,
            USER_SCOPE_ENUM.SENIOR_CUSTOMER,
            USER_SCOPE_ENUM.JUNIOR_OPERATOR,
            USER_SCOPE_ENUM.SENIOR_OPERATOR,
          ].some((scope) => user.scope.includes(scope)) &&
          ![USER_SCOPE_ENUM.PASSWORD_CHANGE, USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING].some((scope) =>
            user.scope.includes(scope)
          );

        if (isCreateCert) {
          try {
            await findCertificate({
              userId: user.id,
              companyId: user.company_id,
            });
          } catch (e) {
            this.userService.dropToken();
            Promise.reject(e);
          }
        }
        return user;
      })
      .then(this.initSuccess)
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  public logout() {
    return this.userService
      .logout()
      .then(() => {
        this.logoutSuccess();
      })
      .catch((err: any) => {
        console.error("logout", err);
      })
      .finally(() => {
        if (this.stores) {
          this.resetAllStores(this.stores);
        }
      });
  }

  showError(error: HttpResponseError) {
    runInAction(() => {
      this.status = "error";
      this.error = error.message;
    });

    // const errorDescription = get(error, 'response.body.error_description');

    // if (errorDescription) {
    //   return Promise.reject(errorDescription);
    // }

    // const errorCode = get(error, 'response.body.error');

    // if (errorCode) {
    //   return Promise.reject(createError('AUTH_ERROR', errorCode));
    // }
    return Promise.reject(error);
  }

  async initSuccess(userInfo: IUserInfo | null) {
    if (!userInfo) {
      throw new Error("Wrong user info");
    }

    runInAction(() => {
      this.userInfo = userInfo;
      this.isAuthorized = true;
      this.status = "success";
    });

    return Promise.resolve(userInfo);
  }

  public setRedirectPathname = (pathname: string) => {
    runInAction(() => {
      this.redirectPathname = pathname;
    });
  };

  public clearRedirectPathname = () => {
    runInAction(() => {
      this.redirectPathname = "";
      this.isAfterLogout = false;
    });
  };

  logoutSuccess() {
    runInAction(() => {
      this.status = "error";
      this.userInfo = undefined;
      this.isAuthorized = false;
      this.isAfterLogout = true;
    });
  }

  resetAllStores(store: IStores) {
    if (!store) return;
    Object.values(store).forEach((store) => store.clear && store.clear());
  }

  get isPending() {
    return this.status === "fetching";
  }

  get isError() {
    return this.status === "error";
  }

  get scope(): UserScope[] {
    if (!this.userInfo) {
      return [];
    }

    if (
      this.userInfo.status === USER_STATUS_ENUM.DRAFT &&
      this.userInfo.scope.includes(USER_SCOPE_ENUM.PASSWORD_CHANGE)
    ) {
      return [USER_SCOPE_ENUM.PASSWORD_CHANGE];
    }

    if (this.userInfo.scope.includes(USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING)) {
      return [USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING];
    }

    return this.userInfo.scope;
  }

  get roles() {
    const scope = this.scope;
    return {
      isSeniorCustomer: scope.includes(USER_SCOPE_ENUM.SENIOR_CUSTOMER),
      isJuniorCustomer: scope.includes(USER_SCOPE_ENUM.JUNIOR_CUSTOMER),
      isSeniorLP: scope.includes(USER_SCOPE_ENUM.SENIOR_LP),
      isJuniorLP: scope.includes(USER_SCOPE_ENUM.JUNIOR_LP),
      isLPRiskManager: scope.includes(USER_SCOPE_ENUM.LP_RISK_MANAGER),
      isSeniorOperator: scope.includes(USER_SCOPE_ENUM.SENIOR_OPERATOR),
      isJuniorOperator: scope.includes(USER_SCOPE_ENUM.JUNIOR_OPERATOR),
      isRiskManager: scope.includes(USER_SCOPE_ENUM.RISK_MANAGER),
      isSystemAdmin: scope.includes(USER_SCOPE_ENUM.SYSTEM_ADMIN),
      isUserAdmin: scope.includes(USER_SCOPE_ENUM.USER_ADMIN),
      isCompanyDocUploading: scope.includes(USER_SCOPE_ENUM.COMPANY_DOC_UPLOADING),
      isPasswordChange: scope.includes(USER_SCOPE_ENUM.PASSWORD_CHANGE),

      isCustomer: scope.includes(USER_SCOPE_ENUM.SENIOR_CUSTOMER) || scope.includes(USER_SCOPE_ENUM.JUNIOR_CUSTOMER),
      isProvider:
        scope.includes(USER_SCOPE_ENUM.SENIOR_LP) ||
        scope.includes(USER_SCOPE_ENUM.JUNIOR_LP) ||
        scope.includes(USER_SCOPE_ENUM.LP_RISK_MANAGER),
      isOperator:
        scope.includes(USER_SCOPE_ENUM.SENIOR_OPERATOR) ||
        scope.includes(USER_SCOPE_ENUM.JUNIOR_OPERATOR) ||
        scope.includes(USER_SCOPE_ENUM.RISK_MANAGER) ||
        scope.includes(USER_SCOPE_ENUM.SYSTEM_ADMIN) ||
        scope.includes(USER_SCOPE_ENUM.USER_ADMIN),
    };
  }
}
